import { useRef, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import UpgradeModal from '../components/UpgradeModal';
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard';
import { executionApi, ExecutionApiError } from './api';
import {
  childKind,
  describeError,
  kindLabels,
  pluralKind,
  Modal,
  useExecutionActive,
} from './ExecutionShared';
import {
  flattenExecution,
  type ExecutionDraft,
  type ExecutionDraftItem,
  type ExecutionNode,
  type ExecutionObjective,
  type ExecutionParent,
  type ExecutionRecommendation,
  type ExecutionReview,
  type ExecutionSnapshot,
} from './model';

export default function ExecutionCoachDialog({
  objective,
  snapshot,
  initialParent,
  onClose,
  onSaved,
  onRecommendation,
}: {
  objective: ExecutionObjective;
  snapshot: ExecutionSnapshot;
  initialParent?: ExecutionNode;
  onClose: () => void;
  onSaved: (message: string) => Promise<void>;
  onRecommendation: (recommendation: ExecutionRecommendation) => void;
}) {
  const active = useExecutionActive();
  const [tab, setTab] = useState<'review' | 'draft'>(initialParent ? 'draft' : 'review');
  const [question, setQuestion] = useState('');
  const [guidance, setGuidance] = useState('');
  const [documentText, setDocumentText] = useState('');
  const nodes = [objective, ...flattenExecution(objective.children)];
  const [parentId, setParentId] = useState(initialParent?.id ?? objective.id);
  const parentNode = nodes.find((node) => node.id === parentId) ?? objective;
  const [review, setReview] = useState<ExecutionReview | null>(null);
  const [draft, setDraft] = useState<ExecutionDraft | null>(null);
  const [items, setItems] = useState<ExecutionDraftItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<'review' | 'draft' | 'apply' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const operation = useRef<string | null>(null);
  const guard = useSubscriptionGuard({
    companyId: snapshot.company.id,
    feature: 'Objective execution coach',
  });
  async function getReview() {
    setBusy('review');
    setError(null);
    try {
      const result = await guard.withSubscriptionGuard(
        () => executionApi.review(snapshot.company.id, objective.id, question.trim()),
        'Objective execution review'
      );
      if (result && active.current) setReview(result);
    } catch (err) {
      if (active.current) setError(describeError(err));
    } finally {
      if (active.current) setBusy(null);
    }
  }
  async function getDraft() {
    if (!snapshot.canEdit) return;
    setBusy('draft');
    setError(null);
    try {
      const parent: ExecutionParent = { type: parentNode.type, id: parentNode.id };
      const result = await guard.withSubscriptionGuard(
        () =>
          executionApi.draft(
            snapshot.company.id,
            objective.id,
            parent,
            guidance.trim(),
            documentText.trim()
          ),
        'Objective focused work drafts'
      );
      if (result && active.current) {
        setDraft(result);
        setItems(result.items);
        setSelected(new Set(result.items.map((item) => item.id)));
        setStale(false);
        operation.current = null;
      }
    } catch (err) {
      if (active.current) setError(describeError(err));
    } finally {
      if (active.current) setBusy(null);
    }
  }
  const updateItem = (id: string, patch: Partial<ExecutionDraftItem>) => {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...patch } : item)));
    operation.current = null;
  };
  async function applyDraft() {
    if (!draft || !selected.size || !snapshot.canEdit || stale) return;
    const chosen = items.filter((item) => selected.has(item.id));
    if (
      chosen.some(
        (item) =>
          !item.title.trim() || !item.expectedContribution.trim() || !item.definitionOfDone.trim()
      )
    ) {
      setError('Each selected item needs a title, expected contribution and definition of done.');
      return;
    }
    const chosenIds = new Set(chosen.map((item) => item.id));
    const missingDependency = chosen.find((item) =>
      item.blockedBy.some(
        (id) => items.some((candidate) => candidate.id === id) && !chosenIds.has(id)
      )
    );
    if (missingDependency) {
      setError(
        `Select the draft dependencies for “${missingDependency.title}”, or remove those dependencies before adding this item.`
      );
      return;
    }
    setBusy('apply');
    setError(null);
    operation.current ??= crypto.randomUUID();
    try {
      const result = await guard.withSubscriptionGuard(
        () =>
          executionApi.apply(snapshot.company.id, {
            operationId: operation.current!,
            objectiveId: objective.id,
            contextHash: draft.contextHash,
            kind: draft.kind,
            parent: draft.parent,
            items: chosen,
          }),
        'Add reviewed execution drafts'
      );
      if (result && active.current) {
        await onSaved(
          `${result.created.length} reviewed ${result.created.length === 1 ? draft.kind : pluralKind(draft.kind)} added to the plan.`
        );
        if (active.current) onClose();
      }
    } catch (err) {
      if (!active.current) return;
      setError(describeError(err));
      if (err instanceof ExecutionApiError && err.status === 409) setStale(true);
    } finally {
      if (active.current) setBusy(null);
    }
  }
  const selectAll = () => {
    setSelected(selected.size === items.length ? new Set() : new Set(items.map((item) => item.id)));
    operation.current = null;
  };
  return (
    <>
      {!guard.showUpgradeModal && (
        <Modal
          wide
          title="Move this objective forward"
          description={`Objective: ${objective.title}. The coach uses the outcome evidence, owners and supporting plan to identify the next useful action.`}
          onClose={onClose}
        >
          <div
            className="execution-view-tabs"
            role="group"
            aria-label="Execution coach mode"
            style={{ marginBottom: 22 }}
          >
            <button type="button" aria-pressed={tab === 'review'} onClick={() => setTab('review')}>
              Review next steps
            </button>
            <button type="button" aria-pressed={tab === 'draft'} onClick={() => setTab('draft')}>
              Draft supporting work
            </button>
          </div>
          {tab === 'review' && (
            <>
              <label htmlFor="execution-coach-question">What should we focus on?</label>
              <textarea
                id="execution-coach-question"
                disabled={!!busy}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                maxLength={6000}
                placeholder="For example: What is most likely to stop us hitting the target, and what should we do this week?"
              />
              <button
                className="execution-button primary"
                type="button"
                onClick={getReview}
                disabled={!!busy}
              >
                {busy === 'review' ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )}
                {busy === 'review'
                  ? 'Reviewing objective…'
                  : review
                    ? 'Refresh review'
                    : 'Review objective'}
              </button>
              {review && (
                <div className="execution-coach-response">
                  <h3>What will move the needle</h3>
                  <p>{review.summary}</p>
                  {review.recommendations.map((recommendation) => (
                    <div className="execution-draft" key={recommendation.id}>
                      <h3>{recommendation.title}</h3>
                      <p>{recommendation.rationale}</p>
                      <p>
                        <strong>Expected contribution:</strong>{' '}
                        {recommendation.expectedContribution}
                      </p>
                      {(snapshot.canEdit ||
                        recommendation.action === 'review_work' ||
                        (recommendation.entityRef.type !== 'objective' &&
                          (recommendation.action === 'record_measure' ||
                            recommendation.action === 'clarify_measure'))) && (
                        <button
                          type="button"
                          className="execution-button"
                          onClick={() => {
                            if (recommendation.action === 'draft_children') {
                              setParentId(recommendation.entityRef.id);
                              setTab('draft');
                            } else {
                              onRecommendation(recommendation);
                            }
                          }}
                        >
                          {recommendation.action === 'draft_children'
                            ? 'Draft next steps'
                            : recommendation.action === 'record_measure' &&
                                recommendation.entityRef.type === 'objective'
                              ? 'Record outcome'
                              : recommendation.action === 'clarify_measure' &&
                                  recommendation.entityRef.type === 'objective'
                                ? 'Set primary measure'
                                : recommendation.action === 'assign_owner'
                                  ? 'Assign owner'
                                  : 'Review this work'}
                        </button>
                      )}
                    </div>
                  ))}
                  {review.risks.length > 0 && (
                    <>
                      <h3>Risks to the objective</h3>
                      <ul>
                        {review.risks.map((risk, index) => (
                          <li key={index}>{risk}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {review.missingEvidence.length > 0 && (
                    <>
                      <h3>Evidence still needed</h3>
                      <ul>
                        {review.missingEvidence.map((evidence, index) => (
                          <li key={index}>{evidence}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  <div className="execution-coach-evidence">
                    Reviewed {new Date(review.generatedAt).toLocaleString('en-AU')}. Review
                    recommendations against current evidence before acting.
                  </div>
                </div>
              )}
            </>
          )}
          {tab === 'draft' && (
            <>
              <label htmlFor="execution-draft-parent">Where should the new work contribute?</label>
              <select
                id="execution-draft-parent"
                value={parentId}
                onChange={(e) => {
                  setParentId(e.target.value);
                  setDraft(null);
                  setItems([]);
                  setSelected(new Set());
                }}
                disabled={!!busy}
              >
                {nodes.map((node) => (
                  <option key={node.id} value={node.id}>
                    {kindLabels[node.type]}: {node.title}
                  </option>
                ))}
              </select>
              <label htmlFor="execution-draft-guidance">
                Guidance for the {childKind(parentNode.type)} drafts
              </label>
              <textarea
                id="execution-draft-guidance"
                disabled={!!busy}
                value={guidance}
                onChange={(e) => setGuidance(e.target.value)}
                maxLength={6000}
                placeholder="What should change for the objective? Add constraints, resources and the time horizon."
              />
              <label htmlFor="execution-draft-document">Supporting evidence (optional)</label>
              <textarea
                id="execution-draft-document"
                disabled={!!busy}
                value={documentText}
                onChange={(e) => setDocumentText(e.target.value)}
                maxLength={50000}
                placeholder="Paste relevant notes or source text to ground the suggestions."
              />
              <button
                type="button"
                className="execution-button primary"
                onClick={getDraft}
                disabled={!!busy || !snapshot.canEdit}
              >
                {busy === 'draft' ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )}
                {busy === 'draft'
                  ? 'Drafting…'
                  : draft
                    ? 'Generate fresh drafts'
                    : `Draft ${pluralKind(childKind(parentNode.type))}`}
              </button>
              {draft && (
                <div className="execution-coach-response">
                  <h3>Review before adding work</h3>
                  <p>{draft.rationale}</p>
                  <p className="execution-dialog-help">
                    Choose only the work you want to add. Edit the contribution, definition of done,
                    owner and date first.
                  </p>
                  {draft.warnings.map((warning, index) => (
                    <div className="execution-notice" key={index}>
                      {warning}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="execution-button"
                    onClick={selectAll}
                    disabled={!!busy}
                  >
                    {selected.size === items.length ? 'Deselect all' : 'Select all'}
                  </button>
                  {items.map((item, index) => (
                    <fieldset
                      className="execution-draft"
                      key={item.id}
                      disabled={!!busy}
                      style={{ minWidth: 0 }}
                    >
                      <div className="execution-draft-heading">
                        <input
                          type="checkbox"
                          id={`draft-selected-${index}`}
                          checked={selected.has(item.id)}
                          onChange={(e) => {
                            setSelected((current) => {
                              const next = new Set(current);
                              if (e.target.checked) next.add(item.id);
                              else next.delete(item.id);
                              return next;
                            });
                            operation.current = null;
                          }}
                          disabled={!!busy}
                        />
                        <label htmlFor={`draft-selected-${index}`}>Add draft {index + 1}</label>
                      </div>
                      <label htmlFor={`draft-title-${index}`}>Title</label>
                      <input
                        id={`draft-title-${index}`}
                        value={item.title}
                        onChange={(e) => updateItem(item.id, { title: e.target.value })}
                        maxLength={255}
                      />
                      <label htmlFor={`draft-description-${index}`}>Description</label>
                      <textarea
                        id={`draft-description-${index}`}
                        value={item.description}
                        onChange={(e) => updateItem(item.id, { description: e.target.value })}
                        maxLength={20000}
                      />
                      <label htmlFor={`draft-contribution-${index}`}>
                        Expected contribution to the objective
                      </label>
                      <textarea
                        id={`draft-contribution-${index}`}
                        value={item.expectedContribution}
                        onChange={(e) =>
                          updateItem(item.id, { expectedContribution: e.target.value })
                        }
                        maxLength={6000}
                      />
                      <label htmlFor={`draft-done-${index}`}>Definition of done</label>
                      <textarea
                        id={`draft-done-${index}`}
                        value={item.definitionOfDone}
                        onChange={(e) => updateItem(item.id, { definitionOfDone: e.target.value })}
                        maxLength={6000}
                      />
                      <div className="execution-dialog-grid">
                        <div>
                          <label htmlFor={`draft-owner-${index}`}>Responsible owner</label>
                          <select
                            id={`draft-owner-${index}`}
                            value={
                              item.assignedPositionId
                                ? `position:${item.assignedPositionId}`
                                : item.assignedTo
                                  ? `user:${item.assignedTo}`
                                  : ''
                            }
                            onChange={(e) =>
                              updateItem(item.id, {
                                assignedTo: e.target.value.startsWith('user:')
                                  ? e.target.value.slice(5)
                                  : null,
                                assignedPositionId: e.target.value.startsWith('position:')
                                  ? e.target.value.slice(9)
                                  : null,
                              })
                            }
                          >
                            <option value="">Unassigned</option>
                            <optgroup label="People">
                              {snapshot.people.map((person) => (
                                <option key={person.id} value={`user:${person.id}`}>
                                  {person.name}
                                </option>
                              ))}
                            </optgroup>
                            {draft.kind !== 'strategy' && (
                              <optgroup label="Positions">
                                {snapshot.positions.map((position) => (
                                  <option key={position.id} value={`position:${position.id}`}>
                                    {position.title} ·{' '}
                                    {position.holders.map((person) => person.name).join(', ') ||
                                      'Position vacant'}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                          </select>
                        </div>
                        <div>
                          <label htmlFor={`draft-date-${index}`}>Target date</label>
                          <input
                            id={`draft-date-${index}`}
                            type="date"
                            value={item.dueDate?.slice(0, 10) ?? ''}
                            onChange={(e) =>
                              updateItem(item.id, { dueDate: e.target.value || null })
                            }
                          />
                        </div>
                        <div>
                          <label htmlFor={`draft-priority-${index}`}>Priority</label>
                          <select
                            id={`draft-priority-${index}`}
                            value={item.priority}
                            onChange={(e) =>
                              updateItem(item.id, {
                                priority: e.target.value as ExecutionDraftItem['priority'],
                              })
                            }
                          >
                            {[
                              'low',
                              'medium',
                              'high',
                              ...(draft.kind === 'task' ? ['critical'] : []),
                            ].map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor={`draft-hours-${index}`}>Own estimated hours</label>
                          <input
                            id={`draft-hours-${index}`}
                            type="number"
                            min={0}
                            max={draft.kind === 'task' ? 999.99 : 99999}
                            step={0.01}
                            value={item.estimatedHours ?? ''}
                            onChange={(e) =>
                              updateItem(item.id, {
                                estimatedHours: e.target.value ? Number(e.target.value) : null,
                              })
                            }
                          />
                        </div>
                      </div>
                      <label htmlFor={`draft-blockers-${index}`}>Blocked by</label>
                      <select
                        id={`draft-blockers-${index}`}
                        multiple
                        value={item.blockedBy}
                        onChange={(e) =>
                          updateItem(item.id, {
                            blockedBy: Array.from(e.target.selectedOptions).map(
                              (option) => option.value
                            ),
                          })
                        }
                      >
                        {[
                          ...nodes.filter((node) => node.type === draft.kind),
                          ...items.filter((candidate) => candidate.id !== item.id),
                        ].map((candidate) => (
                          <option key={candidate.id} value={candidate.id}>
                            {candidate.title}
                          </option>
                        ))}
                      </select>
                      <p className="execution-dialog-help">
                        Select dependencies. Draft dependencies must also be selected for addition.
                      </p>
                    </fieldset>
                  ))}
                  {stale && (
                    <div className="execution-notice error">
                      The objective or supporting plan changed since these drafts were generated.
                      Your edits are retained. Generate fresh drafts before adding work.
                    </div>
                  )}
                  <div className="execution-dialog-footer">
                    <button type="button" className="execution-button" onClick={onClose}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="execution-button primary"
                      onClick={applyDraft}
                      disabled={!!busy || !selected.size || !snapshot.canEdit || stale}
                    >
                      {busy === 'apply'
                        ? 'Adding reviewed work…'
                        : `Add ${selected.size} selected ${selected.size === 1 ? draft.kind : pluralKind(draft.kind)}`}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {error && (
            <div className="execution-notice error" role="alert" style={{ marginTop: 18 }}>
              {error}
            </div>
          )}
        </Modal>
      )}
      <UpgradeModal {...guard.upgradeModalProps} />
    </>
  );
}
